  import Head from 'next/head';
  import { useState, useEffect } from 'react';
  import { useRouter } from 'next/router';
  import Image from 'next/image';
  import Link from 'next/link';
  import { loginUser, kycStatus } from '../services/api';
  import {
    validateToken,
    verifySession,
    safeLocalStorage,
    clearAuthData
  } from '../utils/auth';

  export default function SignIn() {
    const router = useRouter();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // Load remembered email
    useEffect(() => {
      try {
        const rememberedEmail = safeLocalStorage.getItem('email');
        if (rememberedEmail) {
          setEmail(rememberedEmail);
          setRememberMe(true);
        }
      } catch {}
    }, []);

    // Clear error when user starts typing
    useEffect(() => {
      if (!(error && (email || password))) return;
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }, [email, password, error]);

    // Ask the server, not the token — a token for a deleted or revoked user
    // must not be treated as a live session.
    useEffect(() => {
      let cancelled = false;
      (async () => {
        const session = await verifySession();
        if (!cancelled && session.authenticated) router.push('/dashboard');
      })();
      return () => { cancelled = true; };
    }, [router]);

    const validateForm = () => {
      if (!email.trim()) {
        setError('Email address is required');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError('Please enter a valid email address');
        return false;
      }
      if (!password) {
        setError('Password is required');
        return false;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      return true;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      if (!validateForm()) return;

      setIsLoading(true);
      try {
        // Login against backend
        const result = await loginUser({
          email: email.trim().toLowerCase(),
          password: password
        });

        if (result.success && result.token && result.user) {
          // Store auth
          safeLocalStorage.setItem('jwt', result.token);
          try {
            // normalize shape before caching
            const { normalizeUser } = await import('../utils/auth');
            safeLocalStorage.setItem('user', JSON.stringify(normalizeUser(result.user)));
          } catch {
            safeLocalStorage.setItem('user', JSON.stringify(result.user));
          }

                    // Update last_login timestamp on backend
          try {
            await fetch(`${process.env.NEXT_PUBLIC_API_URL}/user/last-login`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${result.token}`
              }
            });
          } catch (err) {
            console.warn('Failed to update last_login:', err);
          }


          // Remember me
          try {
            if (rememberMe) safeLocalStorage.setItem('email', email.trim().toLowerCase());
            else safeLocalStorage.removeItem('email');
          } catch {}

          // Decide the next page based on live investor state.
          // PRIORITY 1: funded LPs go straight to dashboard, period.
          // PRIORITY 2: verified-but-not-yet-funded LPs go to /welcome.
          // FALLBACK: prospects (un-verified) follow the KYC/risk funnel.
          try {
            const apiBase = process.env.NEXT_PUBLIC_API_URL || '/api';
            const statusRes = await fetch(`${apiBase}/accreditation/status`, {
              headers: { 'Authorization': `Bearer ${result.token}`, 'Accept': 'application/json' }
            });
            if (statusRes.ok) {
              const st = await statusRes.json();
              // Funded → dashboard. Bypass all onboarding regardless of localStorage flags.
              if (st && st.investor_status === 'funded') {
                safeLocalStorage.setItem('kycCompleted', 'true');
                safeLocalStorage.setItem('riskCompleted', 'true');
                router.push('/dashboard');
                return;
              }
              // Verified but not yet funded → welcome page (they're in the funnel, done with KYC).
              if (st && st.accredited_status === 'verified') {
                safeLocalStorage.setItem('kycCompleted', 'true');
                router.push('/welcome');
                return;
              }
            }
          } catch (err) {
            console.warn('accreditation/status check failed, falling through to KYC funnel:', err);
          }

          // Prospect / new sign-up path — original KYC funnel preserved.
          try {
            const ks = await kycStatus(); // GET /api/kyc
            if (ks?.success) {
              if (ks.completed) {
                safeLocalStorage.setItem('kycCompleted', 'true');
                const hasRisk =
                  safeLocalStorage.getItem('riskCompleted') === 'true' ||
                  !!safeLocalStorage.getItem('riskProfile');
                router.push(hasRisk ? '/welcome' : '/risk');
              } else {
                router.push('/kyc-welcome');
              }
              return;
            }
            router.push('/kyc-welcome');
          } catch {
            router.push('/kyc-welcome');
          }
        } else {
          throw new Error(result.error || result.message || 'Login failed');
        }
      } catch (err) {
        console.error('Login error:', err);

        // Friendly errors
        let msg = 'Login failed. Please try again.';
        if (err?.name === 'AbortError') {
          msg = 'Connection timeout. Please check your internet and try again.';
        } else if (err?.message?.includes('Failed to fetch')) {
          msg = 'Unable to connect to server. Please check your internet connection.';
        } else if (err?.message?.includes('NetworkError')) {
          msg = 'Network error occurred. Please try again.';
        } else if (err?.status === 401) {
          msg = 'Invalid email or password';
        } else if (err?.status === 429) {
          msg = 'Too many login attempts. Please try again later.';
        } else if (err?.status >= 500) {
          msg = 'Server error. Please try again later.';
        } else if (err?.message) {
          msg = err.message;
        }

        // Clear junk auth if auth-ish error
        if (String(err?.message || '').toLowerCase().includes('invalid')) {
          try { clearAuthData(); } catch {}
        }

        setError(msg);
      } finally {
        setIsLoading(false);
      }
    };

    const handleRememberMeChange = (checked) => {
      setRememberMe(checked);
      if (!checked) {
        try { safeLocalStorage.removeItem('email'); } catch {}
      }
    };

    return (
      <>
        <Head>
          <title>Sign In – KTR Investments</title>
          <meta name="description" content="Access your sophisticated investment platform with institutional-grade portfolio management and AI-powered insights." />
          <meta name="keywords" content="investment, portfolio management, wealth management, sign in, login, KTR" />
          <meta name="robots" content="noindex, nofollow" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>

        <div className="bg-black text-white min-h-screen flex">
          {/* Left side branding omitted for brevity (unchanged) */}
          <div className="hidden lg:flex lg:w-1/2 relative">
            <div className="absolute inset-0 bg-black" />
            <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
              <div className="mb-8">
                <Image src="/ktr_isotipo.svg" alt="KTR Investments" width={64} height={64} className="mb-6" priority />
                <h1 className="text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                  Welcome Back to<br /><span className="text-orange-400">KTR Investments</span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Your sophisticated investment platform awaits. Access institutional-grade portfolio management and AI-powered insights.
                </p>
              </div>
              {/* ... value props unchanged ... */}
            </div>
          </div>

          {/* Right side form */}
          <div className="flex-1 lg:w-1/2 flex flex-col items-center justify-center px-6 lg:px-12 bg-black">
            <div className="w-full max-w-md space-y-8">
              {/* Mobile header / desktop header unchanged ... */}

              {/* Error */}
              {error && (
                <div className="p-4 text-sm text-red-300 bg-red-900/20 border border-red-700 rounded-lg" role="alert" aria-live="polite">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium">Sign In Failed</p>
                      <p className="text-red-300/90">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Form */}
              <div className="bg-black border border-gray-800 p-8 rounded-lg">
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                      placeholder="Enter your email"
                      disabled={isLoading}
                      autoComplete="email"
                      required
                      maxLength={254}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 pr-12"
                        placeholder="Enter your password"
                        disabled={isLoading}
                        autoComplete="current-password"
                        required
                        minLength={8}
                        maxLength={128}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                        disabled={isLoading}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {showPassword ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59" />
                          ) : (
                            <>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => handleRememberMeChange(e.target.checked)}
                        className="h-4 w-4 text-orange-500 focus:ring-orange-500 focus:ring-2 bg-black border-gray-800 rounded transition-colors"
                        disabled={isLoading}
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300 cursor-pointer">
                        Remember me
                      </label>
                    </div>
                    <div className="text-sm">
                      <Link href="/forgot-password" className="text-orange-400 hover:text-orange-300 transition-colors font-medium">
                        Forgot password?
                      </Link>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-4 rounded-lg font-semibold text-lg transition-all duration-200 ${
                      isLoading ? 'bg-gray-800 cursor-not-allowed opacity-50 text-gray-400'
                                : 'bg-orange-500 hover:bg-orange-600 text-black'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

                {/* Sign Up link */}
                <div className="text-center pt-6 border-t border-gray-800 mt-6">
                  <p className="text-sm text-gray-400">
                    New to KTR Investments?{' '}
                    <Link href="/sign-up" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
                      Create account
                    </Link>
                  </p>
                </div>
              </div>

              {/* Legal Footer (unchanged) */}
              <div className="text-center pt-6">
                <p className="text-xs text-gray-500 leading-relaxed">
                  By signing in, you agree to our{' '}
                  <Link href="/terms" className="text-gray-400 hover:text-gray-300 transition-colors">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" className="text-gray-400 hover:text-gray-300 transition-colors">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
